import { z } from 'zod';
import { ecosystemSchema } from './ecosystem';
import { severityLevelSchema } from './advisory';

/**
 * Flow 3 — manifest audit. The client parses a manifest into dependencies (see
 * the manifest parsers in this package) and POSTs them; the Worker resolves any
 * version ranges, fans out to the batch provider endpoints, and returns one row
 * per dependency.
 */

/** Hard cap on dependencies per audit — keeps us inside Worker CPU limits. */
export const AUDIT_MAX_DEPENDENCIES = 500;

/**
 * One dependency extracted from a manifest.
 *
 * `version` is the exact version to audit. It is null on the way in when the
 * manifest gave a range instead of a pin — the Worker then simplifies the
 * `requirement` to the highest published version under its upper bound and
 * fills `version` in on the way out (null there means resolution failed).
 */
export const auditDependencySchema = z.object({
  system: ecosystemSchema,
  name: z.string().min(1).max(500),
  version: z.string().min(1).max(200).nullish().default(null),
  /** The raw requirement string from the manifest (e.g. `^4.17.0`), if any. */
  requirement: z.string().max(200).nullish().default(null),
});
export type AuditDependency = z.infer<typeof auditDependencySchema>;

/** A dependency is only auditable if we can get a version out of it. */
export const auditRequestDependencySchema = auditDependencySchema.refine(
  (d) => (d.version ?? null) !== null || (d.requirement ?? '') !== '',
  { message: 'a dependency needs either a version or a requirement range' },
);

/** Body for `POST /api/v1/audit`. */
export const auditRequestSchema = z.object({
  dependencies: z.array(auditRequestDependencySchema).min(1).max(AUDIT_MAX_DEPENDENCIES),
});
export type AuditRequest = z.infer<typeof auditRequestSchema>;

export const auditStatusSchema = z.enum(['vulnerable', 'deprecated', 'ok', 'unknown']);
export type AuditStatus = z.infer<typeof auditStatusSchema>;

/** Compact advisory reference shown in an audit row. */
export const auditAdvisoryRefSchema = z.object({
  id: z.string().min(1),
  severity: severityLevelSchema,
  cvssScore: z.number().nullable(),
  summary: z.string().nullable(),
  /** Fix versions that apply to this dependency, if known. */
  fixedVersions: z.array(z.string()),
});
export type AuditAdvisoryRef = z.infer<typeof auditAdvisoryRefSchema>;

/**
 * How the audited version was arrived at:
 *   - `exact` — the manifest pinned it;
 *   - `resolved` — simplified from a range to the newest version it allows;
 *   - `unresolved` — a range we could not simplify (unparseable constraint,
 *     unknown package, or nothing published under the upper bound).
 */
export const auditResolutionSchema = z.enum(['exact', 'resolved', 'unresolved']);
export type AuditResolution = z.infer<typeof auditResolutionSchema>;

/** One row of the audit report — verdict for a single dependency. */
export const auditRowSchema = z.object({
  dependency: auditDependencySchema,
  /** `vulnerable` wins over `deprecated`; `unknown` = package/version not found. */
  status: auditStatusSchema,
  resolution: auditResolutionSchema,
  isDeprecated: z.boolean(),
  advisories: z.array(auditAdvisoryRefSchema),
});
export type AuditRow = z.infer<typeof auditRowSchema>;

/** Response for `POST /api/v1/audit`. */
export const auditReportSchema = z.object({
  rows: z.array(auditRowSchema),
  summary: z.object({
    total: z.number().int().min(0),
    vulnerable: z.number().int().min(0),
    deprecated: z.number().int().min(0),
    ok: z.number().int().min(0),
    unknown: z.number().int().min(0),
  }),
});
export type AuditReport = z.infer<typeof auditReportSchema>;
